import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import PosViewToggle from "@/components/PosViewToggle";
import OfflineBanner from "@/components/OfflineBanner";
import { useConnectivity } from "@/lib/offline/connectivity";
import { syncPending } from "@/lib/offline/sync";
import BillingBanner from "@/components/BillingBanner";
import PinRecoveryBanner from "@/components/PinRecoveryBanner";
import QuickLogin from "@/pages/QuickLogin";
import { isTrustedDevice, fetchQuickStatus } from "@/lib/device";
import { useAuth } from "@/context/AuthContext";
import { startDiag } from "@/lib/diag";
import { useModules } from "@/lib/modules";
import api from "@/lib/api";
import SetupWizard from "@/components/SetupWizard";
import ServiceDetailsGate from "@/components/ServiceDetailsGate";
import GuidedTour from "@/components/GuidedTour";
import AskEli from "@/components/eli/AskEli";
import { useWhatsNew, WhatsNewPopover, WhatsNewSheet } from "@/components/WhatsNew";
import {
  LayoutDashboard, ShoppingCart, Package, Library, Users, PieChart, Truck,
  Wallet, BellRing, ScrollText, PackageCheck, UserCog, Settings, LogOut, Moon, Sun, Menu, X, Zap, Lock,
  FileText, Receipt, BarChart3, CreditCard, ClipboardList, ScanLine, ShieldCheck, LifeBuoy, Sparkles, UploadCloud,
  WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import EnvBadge from "@/components/EnvBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { logoSrc } from "@/lib/branding";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/pos", label: "POS Register", icon: ShoppingCart },
  { to: "/sales-history", label: "Sales History", icon: ScrollText },
  { to: "/offline-sync", label: "Offline Sync", icon: WifiOff, roles: ["owner", "manager"] },
  { to: "/shift", label: "Shift Report", icon: ClipboardList },
  { to: "/pickups", label: "Pickups", icon: PackageCheck },
  { to: "/quotations", label: "Quotations", icon: FileText, feature: "quotations" },
  { to: "/inventory", label: "Inventory", icon: Package, roles: ["owner", "manager"] },
  { to: "/suppliers", label: "Suppliers", icon: Truck, feature: "suppliers", roles: ["owner", "manager"] },
  { to: "/import", label: "Import Data", icon: UploadCloud, roles: ["owner", "manager"] },
  { to: "/library", label: "Hardware Library", icon: Library, feature: "hardware_library", roles: ["owner", "manager"] },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/ar-aging", label: "AR Aging", icon: PieChart, feature: "customers_credit", roles: ["owner", "manager"] },
  { to: "/payments", label: "Payments", icon: Wallet, feature: "customers_credit" },
  { to: "/reminders", label: "Reminders", icon: BellRing, feature: "reminders", roles: ["owner", "manager"] },
  { to: "/expenses", label: "Expenses", icon: Receipt, feature: "expenses", roles: ["owner", "manager"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["owner", "manager"] },
  { to: "/promotions", label: "Promotions", icon: Sparkles, feature: "special_promotions", roles: ["owner", "manager"] },
  { to: "/payments-setup", label: "WiPay Setup", icon: CreditCard, feature: "wipay", roles: ["owner"] },
  { to: "/wipay-reconciliation", label: "WiPay Recon", icon: ScanLine, feature: "wipay", roles: ["owner", "manager"] },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, roles: ["owner", "manager"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["owner", "manager"] },
  { to: "/staff", label: "Staff", icon: UserCog, roles: ["owner"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["owner", "manager"] },
  { to: "/billing", label: "Subscription", icon: CreditCard, roles: ["owner"] },
  { to: "/help", label: "Help & Support", icon: LifeBuoy },
  { to: "/security", label: "Security", icon: ShieldCheck },
];

const roleBadge = {
  owner: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  manager: "bg-violet-500/15 text-violet-500 border-violet-500/30",
  cashier: "bg-sky-500/15 text-sky-500 border-sky-500/30",
};

export default function Layout({ children }) {
  const { user, business, logout, tourOpen, openTour } = useAuth();
  const modules = useModules();
  const conn = useConnectivity();
  // Back online -> drain the offline sales queue for this tenant + register (idempotent; safe on reload/retry).
  useEffect(() => {
    if (conn.status === "ONLINE" && user && user.role !== "superadmin") {
      const reg = localStorage.getItem("neec_register_token") || localStorage.getItem("neec_device_token") || "device";
      syncPending(user.business_id, reg.slice(0, 16)).catch(() => {});
    }
  }, [conn.status, user]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("neec_theme") !== "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("neec_theme", dark ? "dark" : "light");
  }, [dark]);
  // Fashion & Beauty tenants get the slate-blue / charcoal / white token set; Hardware tenants keep the emerald theme untouched.
  useEffect(() => {
    if (business?.is_fashion) document.documentElement.setAttribute("data-theme", "fashion");
    else document.documentElement.removeAttribute("data-theme");
    return () => document.documentElement.removeAttribute("data-theme");
  }, [business?.is_fashion]);

  useEffect(() => {
    if (user?.role === "owner" && business?.onboarded && !user?.tour_completed && !tourOpen) {
      openTour();
    }
  }, [user, business]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    try {
      const { data } = await api.get("/shifts/current");
      if (data && data.status === "open") {
        const ok = window.confirm("Your cashier shift is still OPEN. You should close and reconcile it before logging out. Log out anyway?");
        if (!ok) return;
      }
    } catch {}
    await logout();
    nav("/login");
  };

  // Quick Lock / Switch User (trusted terminals only). Locking hides all data and keeps the
  // register state; it never closes a shift or logs out. Auto-lock after inactivity per role.
  const [locked, setLocked] = useState(null);
  const wn = useWhatsNew();
  const [wnOpen, setWnOpen] = useState(false);
  const [deviceTrusted, setDeviceTrusted] = useState(isTrustedDevice());
  useEffect(() => {
    const sync = () => { if (isTrustedDevice()) { setDeviceTrusted(true); return; } fetchQuickStatus().then((s) => setDeviceTrusted(!!s.quick_login_available)); };
    sync(); window.addEventListener("neec-device-trusted", sync);
    return () => window.removeEventListener("neec-device-trusted", sync);
  }, [pathname, locked]);
  const trusted = deviceTrusted && user?.role !== "superadmin";
  const lock = async (auto = false) => {
    if (!trusted || locked) return;
    let shiftOpen = false;
    try { const { data } = await api.get("/shifts/current"); shiftOpen = !!(data && data.status === "open"); } catch {}
    api.post("/auth/quick/lock", { auto }).catch(() => {});
    setLocked({ id: user.id, name: user.name, role: user.role, initials: (user.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase(), has_login_pin: true, shift_open: shiftOpen });
  };
  useEffect(() => {
    if (!trusted || locked) return undefined;
    const mins = (business?.auto_lock_minutes || {})[user?.role] ?? ({ cashier: 5, manager: 10, owner: 15 }[user?.role] ?? 15);
    if (!mins) return undefined;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => lock(true), mins * 60000); };
    const evs = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [trusted, locked, business?.auto_lock_minutes, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const lockRef = useRef(lock); lockRef.current = lock;   // latest lock() without re-subscribing on every render
  useEffect(() => {
    const h = (e) => { if (e.key === "F12" && e.shiftKey) { e.preventDefault(); lockRef.current(false); } };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => { if (user && user.role !== "superadmin") startDiag(); }, [user?.id, user?.role]);

  // Module availability (centralized layer) AND role check stay separate concerns.
  // Reports nav link: hidden for a Manager when the Owner has turned off "Manager Access to
  // Reports" (Settings -> Permissions). Owner is never affected; backend is the real enforcer.
  const items = NAV.filter((i) => (!i.feature || modules.isAvailable(i.feature))
    && (!i.roles || i.roles.includes(user?.role))
    && !(i.to === "/reports" && user?.role === "manager" && business?.managers_can_access_reports === false));

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <BrandLogo business={business} size={40} />
          <div className="min-w-0">
            <p className="font-head font-bold leading-tight truncate">{business?.name || "NEEC POS"}</p>
            <p className="text-[10px] text-emerald-500 font-semibold tracking-wider flex items-center gap-1">
              <Zap className="w-3 h-3" /> POWERED BY NEEC POS
            </p>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        {items.map((i) => {
          const Icon = i.icon;
          return (
            <NavLink key={i.to} to={i.to} end={i.to === "/"} onClick={() => setOpen(false)}
              data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors
                 ${isActive ? "bg-accent text-accent-foreground shadow-lg shadow-accent/20"
                            : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Icon className="w-[18px] h-[18px]" /> {i.label}
            </NavLink>
          );
        })}
        {user?.role !== "superadmin" && (
          <button type="button" onClick={() => { setWnOpen(true); setOpen(false); }} data-testid="nav-whats-new"
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <span className="relative"><Sparkles className="w-[18px] h-[18px]" />{wn.unseen.length > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-background" data-testid="nav-whats-new-dot" />}</span> What's New
          </button>
        )}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center font-head font-bold">
            {(user?.name || "U")[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-md border capitalize ${roleBadge[user?.role]}`}>{user?.role}</span>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" title="Log out (ends your session)"
            className="p-2 rounded-lg hover:bg-secondary text-muted-foreground"><LogOut className="w-4 h-4" /></button>
        </div>
        {trusted && (
          <button onClick={() => lock(false)} data-testid="lock-register-btn" title="Lock Register (Shift + F12). Hides all data, keeps your shift open"
            className="mt-1 w-full h-11 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
            <Lock className="w-4 h-4" /> Lock Register <span className="text-[10px] font-normal opacity-70 hidden lg:inline">Shift + F12</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {user?.role === "owner" && business && !business.service_details_reviewed && !business.offline_snapshot && <ServiceDetailsGate />}
      {user?.role === "owner" && business && business.service_details_reviewed && !business.onboarded && <SetupWizard />}
      <GuidedTour />
      <AskEli />
      <aside className="hidden lg:flex w-64 border-r border-border bg-card flex-col shrink-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-card border-r border-border"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        {user?.is_demo && (
          <div className="bg-blue-600 text-white text-center text-xs sm:text-sm font-semibold py-2 px-4" data-testid="demo-banner">
            NEEC POS Demo Environment · Sample data only · Explore freely; sensitive settings are locked
          </div>
        )}
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur flex items-center justify-between px-4 lg:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-secondary" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <BrandLogo business={business} size={34} className="hidden sm:flex" />
            <div className="min-w-0">
              <p className="font-head font-bold text-lg leading-none truncate">{business?.name}</p>
              <div className="flex items-center gap-2 mt-0.5"><p className="text-xs text-muted-foreground hidden sm:block">Modular Business Management</p><EnvBadge /></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              {business?.currency || "JMD $"}
            </span>
            {pathname === "/pos" && <PosViewToggle userId={user?.id} />}
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto neec-grid-bg relative">
          <OfflineBanner />
          <BillingBanner />
          <PinRecoveryBanner />
          {locked && <div className="fixed inset-0 z-[100] bg-background" data-testid="lock-overlay"><QuickLogin mode="lock" lockedUser={locked} onUnlocked={(u) => { const switched = u?.id !== locked.id; setLocked(null); if (switched) nav("/", { replace: true }); }} /></div>}
          {!locked && !tourOpen && business?.onboarded !== false && <WhatsNewPopover queue={wn.queue} ack={wn.ack} onOpenHistory={() => setWnOpen(true)} />}
          <WhatsNewSheet open={wnOpen} onOpenChange={setWnOpen} ack={wn.ack} />
          {business?.show_logo_watermark && logoSrc(business) && (
            <img src={logoSrc(business)} alt="" aria-hidden="true" data-testid="brand-watermark"
              className="pointer-events-none select-none fixed bottom-10 right-10 w-72 max-w-[40vw] opacity-[0.04] object-contain z-0" />
          )}
          <div key={user?.id || "anon"} className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-up relative z-10" data-testid="page-root" data-role={user?.role}>{children}</div>
        </main>
      </div>
    </div>
  );
}
